import debounce from 'transmute/debounce';
import { useState, useEffect, useCallback, useMemo } from 'react';

export type SearchFunction<Data> = (search: {
  query: string;
  offset: number;
  count: number;
}) => Promise<Data>;

export const DEBOUNCE_DELAY = 200;
const buildQuery = (searchQuery: string) => ({
  query: searchQuery,
  count: 50,
  offset: 0,
});

const useContentSearch = <Data, TransformData extends undefined | Function>(
  search: SearchFunction<Data>,
  transformData: TransformData,
  onSearchChange?: VoidFunction,
  initialSearchQuery: string | null = ''
) => {
  const [data, setData] = useState<Data | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');

  const performSearch = useCallback(
    (_searchQuery: string) => {
      search(buildQuery(_searchQuery))
        .then((results: Data) => {
          setData(results);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [search]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(DEBOUNCE_DELAY, (_searchQuery: string) => {
        performSearch(_searchQuery);
        if (onSearchChange) onSearchChange();
      }),
    [onSearchChange, performSearch]
  );

  useEffect(() => {
    if (searchQuery.length === 0) {
      debouncedSearch.cancel();
      performSearch(searchQuery);
    } else {
      debouncedSearch(searchQuery);
    }
  }, [debouncedSearch, searchQuery, performSearch]);

  type Options = TransformData extends (data: Data | null) => infer Transformed
    ? Transformed
    : TransformData extends undefined
    ? Data
    : unknown;
  const options = useMemo<Options>(
    () => (transformData ? transformData(data) : data),
    [data, transformData]
  );
  return [data, searchQuery, setSearchQuery, options, isLoading] as const;
};

export default useContentSearch;
